import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Undo2, Redo2, Eraser, Paintbrush, Wand2, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import { useUploadAssetMutation } from '../assets/assetsApi';
import { uploadAssetDeduplicated } from '../assets/assetUploadManager';
import { toast } from 'react-hot-toast';

interface BackgroundEraserModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  onApply: (newImageUrl: string) => void;
}

export const BackgroundEraserModal: React.FC<BackgroundEraserModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  onApply,
}) => {
  const [uploadAsset] = useUploadAssetMutation();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const originalCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Tools: 'auto' (magic wand / color key) or 'brush' (manual erase/restore)
  const [activeTab, setActiveTab] = useState<'auto' | 'manual'>('auto');
  const [brushMode, setBrushMode] = useState<'erase' | 'restore'>('erase');
  const [brushSize, setBrushSize] = useState<number>(30);
  const [tolerance, setTolerance] = useState<number>(20);
  const [zoom, setZoom] = useState<number>(1);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Undo / Redo history stack (ImageData snapshots)
  const historyRef = useRef<ImageData[]>([]);
  const historyIndexRef = useRef<number>(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Brush drawing state
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);

  const saveHistoryState = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    // Cut off redo history
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push(imgData);
    historyIndexRef.current = historyRef.current.length - 1;

    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(false);
  }, []);

  const handleUndo = () => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.putImageData(historyRef.current[historyIndexRef.current], 0, 0);
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
  };

  const handleRedo = () => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current += 1;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.putImageData(historyRef.current[historyIndexRef.current], 0, 0);
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
  };

  // Load image into canvas on open
  useEffect(() => {
    if (!isOpen) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      // Save a pristine backing canvas for 'restore' brush
      const origCanvas = document.createElement('canvas');
      origCanvas.width = img.naturalWidth;
      origCanvas.height = img.naturalHeight;
      const origCtx = origCanvas.getContext('2d', { willReadFrequently: true });
      if (origCtx) origCtx.drawImage(img, 0, 0);
      originalCanvasRef.current = origCanvas;

      // Initialize history
      historyRef.current = [ctx.getImageData(0, 0, canvas.width, canvas.height)];
      historyIndexRef.current = 0;
      setCanUndo(false);
      setCanRedo(false);
      setZoom(1);
    };
    img.src = imageUrl;
  }, [isOpen, imageUrl]);

  // Color-Key / Auto Background Removal Algorithm (Fast, Non-AI)
  const applyAutoColorKey = () => {
    const canvas = canvasRef.current;
    const origCanvas = originalCanvasRef.current;
    if (!canvas || !origCanvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const origCtx = origCanvas.getContext('2d', { willReadFrequently: true });
    if (!ctx || !origCtx) return;

    // Reset from original first so tolerance change adjusts predictably
    ctx.drawImage(origCanvas, 0, 0);

    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imgData.data;

    // Sample background color from top-left (0,0) and top-right (w-1, 0)
    const targetR = (data[0] + data[(canvas.width - 1) * 4]) / 2;
    const targetG = (data[1] + data[(canvas.width - 1) * 4 + 1]) / 2;
    const targetB = (data[2] + data[(canvas.width - 1) * 4 + 2]) / 2;

    const threshold = (tolerance / 100) * 441.67; // Max Euclidean distance sqrt(255^2*3)

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      const dist = Math.sqrt(
        (r - targetR) * (r - targetR) +
        (g - targetG) * (g - targetG) +
        (b - targetB) * (b - targetB)
      );

      if (dist <= threshold) {
        data[i + 3] = 0; // Transparent
      }
    }

    ctx.putImageData(imgData, 0, 0);
    saveHistoryState();
  };

  const handleResetToOriginal = () => {
    const canvas = canvasRef.current;
    const origCanvas = originalCanvasRef.current;
    if (!canvas || !origCanvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(origCanvas, 0, 0);
    saveHistoryState();
  };

  // Manual Canvas 2D Brush Drawing
  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const drawBrushStroke = (x: number, y: number) => {
    const canvas = canvasRef.current;
    const origCanvas = originalCanvasRef.current;
    if (!canvas || !origCanvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.save();
    if (brushMode === 'erase') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,1)';
      ctx.fill();
    } else {
      // Restore Mode: sample circular clip from pristine original canvas
      ctx.beginPath();
      ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(origCanvas, 0, 0);
    }
    ctx.restore();
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (activeTab !== 'manual') return;
    isDrawingRef.current = true;
    const { x, y } = getCanvasCoordinates(e);
    lastPosRef.current = { x, y };
    drawBrushStroke(x, y);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current || activeTab !== 'manual') return;
    const { x, y } = getCanvasCoordinates(e);

    // Linear interpolate between lastPos and current to avoid dashed brush strokes
    if (lastPosRef.current) {
      const dist = Math.hypot(x - lastPosRef.current.x, y - lastPosRef.current.y);
      const steps = Math.ceil(dist / (brushSize / 4));
      for (let i = 1; i <= steps; i++) {
        const ix = lastPosRef.current.x + ((x - lastPosRef.current.x) * i) / steps;
        const iy = lastPosRef.current.y + ((y - lastPosRef.current.y) * i) / steps;
        drawBrushStroke(ix, iy);
      }
    } else {
      drawBrushStroke(x, y);
    }
    lastPosRef.current = { x, y };
  };

  const handleMouseUp = () => {
    if (isDrawingRef.current) {
      isDrawingRef.current = false;
      lastPosRef.current = null;
      saveHistoryState();
    }
  };

  const handleApply = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setIsProcessing(true);
    const toastId = toast.loading('Saving transparent image...');

    try {
      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error('Failed to export image', { id: toastId });
          setIsProcessing(false);
          return;
        }

        const localUrl = URL.createObjectURL(blob);
        onApply(localUrl);

        // Upload to backend asset storage in background with deduplication & in-flight tracking
        const fileName = `Erased_BG_${Date.now()}.png`;
        uploadAssetDeduplicated(uploadAsset, blob, fileName)
          .then((asset) => {
            if (asset?.url) {
              console.log('[BackgroundEraser] Uploaded erased image to cloud:', asset.url);
              onApply(asset.url);
            }
          })
          .catch((err) => {
            console.warn('[BackgroundEraser] Upload error:', err);
          });

        toast.success('Background erased successfully', { id: toastId });
        setIsProcessing(false);
        onClose();
      }, 'image/png');
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save image', { id: toastId });
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="flex flex-col bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden border border-gray-100 max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
              <Eraser className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Background Eraser</h2>
              <p className="text-xs text-gray-500">Auto-remove uniform backgrounds or manually erase pixels</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Undo"
            >
              <Undo2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleRedo}
              disabled={!canRedo}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Redo"
            >
              <Redo2 className="w-4 h-4" />
            </button>
            <div className="w-px h-5 bg-gray-200 mx-1" />
            <button
              onClick={onClose}
              className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Mode Selector & Toolbar */}
        <div className="flex items-center justify-between px-6 py-3 bg-gray-50/90 border-b border-gray-100 flex-wrap gap-3">
          {/* Tabs */}
          <div className="flex items-center gap-1 bg-gray-200/60 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setActiveTab('auto')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                activeTab === 'auto'
                  ? 'bg-white text-purple-700 shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Wand2 className="w-3.5 h-3.5" />
              <span>Auto Magic Wand</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('manual')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                activeTab === 'manual'
                  ? 'bg-white text-purple-700 shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Paintbrush className="w-3.5 h-3.5" />
              <span>Manual Brush</span>
            </button>
          </div>

          {/* Tab Specific Controls */}
          {activeTab === 'auto' ? (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-600">Tolerance:</span>
                <input
                  type="range"
                  min="5"
                  max="80"
                  value={tolerance}
                  onChange={(e) => setTolerance(Number(e.target.value))}
                  className="w-28 accent-purple-600"
                />
                <span className="text-xs font-mono text-gray-500 w-8">{tolerance}%</span>
              </div>
              <button
                type="button"
                onClick={applyAutoColorKey}
                className="px-3.5 py-1.5 rounded-md text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 transition-colors shadow-xs"
              >
                Remove Background
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              {/* Erase vs Restore */}
              <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md p-0.5">
                <button
                  type="button"
                  onClick={() => setBrushMode('erase')}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-all ${
                    brushMode === 'erase' ? 'bg-red-50 text-red-600 font-bold' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Eraser className="w-3 h-3" />
                  <span>Erase</span>
                </button>
                <button
                  type="button"
                  onClick={() => setBrushMode('restore')}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-all ${
                    brushMode === 'restore' ? 'bg-green-50 text-green-600 font-bold' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Paintbrush className="w-3 h-3" />
                  <span>Restore</span>
                </button>
              </div>

              {/* Brush Size */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-600">Size:</span>
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  className="w-24 accent-purple-600"
                />
                <span className="text-xs font-mono text-gray-500 w-8">{brushSize}px</span>
              </div>
            </div>
          )}

          {/* Reset & Zoom */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
              className="p-1 text-gray-500 hover:bg-gray-200 rounded"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs font-mono text-gray-600 w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
              className="p-1 text-gray-500 hover:bg-gray-200 rounded"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={handleResetToOriginal}
              className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800 ml-2"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reset</span>
            </button>
          </div>
        </div>

        {/* Checkerboard Workspace Area */}
        <div
          className="relative flex items-center justify-center p-6 flex-1 min-h-[420px] max-h-[550px] overflow-auto select-none"
          style={{
            backgroundImage: `linear-gradient(45deg, #e5e7eb 25%, transparent 25%), 
              linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), 
              linear-gradient(45deg, transparent 75%, #e5e7eb 75%), 
              linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)`,
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
            backgroundColor: '#ffffff',
          }}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <canvas
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'center center',
              cursor: activeTab === 'manual' ? (brushMode === 'erase' ? 'crosshair' : 'cell') : 'default',
            }}
            className="max-h-[500px] max-w-full shadow-lg border border-gray-200/50 rounded-xs transition-transform duration-100"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-t border-gray-100">
          <p className="text-xs text-gray-500">
            {activeTab === 'auto'
              ? 'Adjust tolerance to remove solid/light background pixels instantly'
              : `Brush mode: ${brushMode.toUpperCase()} | Drag over the image to adjust transparency`}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={isProcessing}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 shadow-sm transition-all disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              <span>{isProcessing ? 'Saving...' : 'Apply Cutout'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};